import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Info, RefreshCw, Trash2, Copy, Check, KeyRound, X, Send, Pencil, History,
  CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import {
  useWebhooksApi, buildEventAsync, eventLabel, endpointState, formatSuccessRate,
  WEBHOOK_NAME_MAX, DELIVERY_STATUS_COLORS, ENDPOINT_STATE_COLORS,
} from './config/webhooks';
import { Badge } from './config';
import { formatDateTime } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import TextInput from '../../components/ui/TextInput.jsx';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import Button from '../../components/ui/Button';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import WebhookDeliveriesDrawer from './WebhookDeliveriesDrawer';

// Entegrasyonlar > Webhook'lar (Public API Plan 03, Görev 7; tasarım belgesi §6).
// PM ayarlarındaki Faz I sekmesinin (liste + detay paneli) YERİNİ alır; yönetim PA
// modülündedir (§6.5).
//
// Liste EmployeesPage / ApiKeysTab aynasıdır: başlık ve "Yeni" düğmesi DataTable'ın İÇİNDE,
// oluşturma/düzenleme GenericFormDrawer ile (modal DEĞİL), KABUK deseninde (schema=[] +
// children): olay seçicisi aramalı AsyncGenericSelect'tir (60+ olay — düz liste kullanılamaz).
//
// SIR ARTIK ÜRETİLİR (§6.1): formda sır alanı YOKTUR. Oluşturma ve "sırrı yenile" yanıtındaki
// `plainSecret` yalnız tablonun üstündeki "bir kez göster" panelinde yaşar; panel kapanınca
// state sıfırlanır ve bir daha gösterilmez — liste yalnız maskeyi bilir. Panel bir çekmece
// DEĞİL (ApiKeysTab ile aynı gerekçe).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* İç içe scroll yasak — tek kaydırma DataTable satırlarıdır. */
  min-height: 0;
`;

const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary.main};
  background: #EEF6FD;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.4;

  svg { flex-shrink: 0; margin-top: 1px; color: ${({ theme }) => theme.colors.primary.main}; }
`;

const NoticeBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EmptyLine = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const RevealPanel = styled.div`
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 6px;
  border-left: 3px solid #FFC107;
  background: #FFF8E6;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.4;
`;

const RevealHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;

  > svg { flex-shrink: 0; color: #B7791F; }
`;

const RevealTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const RevealText = styled.p`
  margin: 0 0 4px;
`;

const SecretRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const PlainSecret = styled.div`
  flex: 1;
  min-width: 0;
  font-family: monospace;
  font-size: 13px;
  background: #fff;
  border: 1px dashed #C5CCD6;
  border-radius: 6px;
  padding: 8px 12px;
  word-break: break-all;
  user-select: all;
`;

const TestResult = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  ${({ $tone }) => {
    if ($tone === 'success') return 'background: #DCFCE7; color: #166534;';
    if ($tone === 'warning') return 'background: #FFF7ED; color: #9A3412;';
    return 'background: #FEF2F2; color: #B3261E;';
  }}

  > span { flex: 1; min-width: 0; }
  > svg { flex-shrink: 0; margin-top: 1px; }
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

// Alanın altındaki açıklama: POZİTİF üst marj (UI altın kuralı 4).
const Hint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 6px 0 0;
`;

const DrawerNotice = styled.div`
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #FECACA;
  background: #FEF2F2;
  color: #B3261E;
  font-size: 13px;
  line-height: 1.45;
`;

const UrlText = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const Muted = styled.span`
  font-size: 12px;
  color: #6c757d;
`;

const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;

// Onay diyaloğunda HANGİ ucun etkileneceği (ad · adres) — çeviriye gömülmez.
const TargetRef = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};

  span { font-family: monospace; }
`;

const emptyDraft = () => ({
  id: null, name: '', url: '', eventKeys: [], isEnabled: true, autoDisabledAt: null,
});

const isHttpsUrl = (value) => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

export default function WebhooksTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { canWrite } = useAuth();
  const canManage = Boolean(canWrite('PA'));

  const {
    fetching, failed, data, catalog, create, update, remove, rotate, test,
  } = useWebhooksApi();
  const eventAsync = useMemo(() => buildEventAsync(t, catalog), [t, catalog]);

  // Oluşturma/düzenleme çekmecesinin taslağı (KABUK deseni).
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [nameError, setNameError] = useState('');
  const [urlError, setUrlError] = useState('');

  // { name, plainSecret, rotated } — YALNIZ panel açıkken; kapanınca null.
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied] = useState(false);

  const [rotateTarget, setRotateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testState, setTestState] = useState(null);   // { name, kind, responseStatus?, error? }
  const [testingId, setTestingId] = useState(null);
  const [deliveriesTarget, setDeliveriesTarget] = useState(null);

  const openCreate = useCallback(() => {
    setDraft(emptyDraft());
    setNameError('');
    setUrlError('');
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setDraft({
      id: row.id,
      name: row.name,
      url: row.url,
      eventKeys: row.eventKeys || [],
      isEnabled: row.isEnabled,
      autoDisabledAt: row.autoDisabledAt,
    });
    setNameError('');
    setUrlError('');
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => setFormOpen(false), []);

  const setField = useCallback((name, value) => setDraft((d) => ({ ...d, [name]: value })), []);

  const reveal = useCallback((created, rotated) => {
    setCopied(false);
    setRevealed({ name: created.name, plainSecret: created.plainSecret, rotated });
  }, []);

  const closeReveal = useCallback(() => {
    setRevealed(null);
    setCopied(false);
  }, []);

  // false dönerse çekmece açık kalır (geçersiz form kapısı — düğme kilitlemek değil).
  // İstemci ipucu; asıl kapı sunucudadır (webhook.nameRequired / invalidUrl / blockedUrl ...).
  const handleSave = useCallback(async () => {
    const name = draft.name.trim();
    const url = draft.url.trim();
    const nextNameError = name ? '' : t('responseErrors.webhook.nameRequired');
    const nextUrlError = isHttpsUrl(url) ? '' : t('responseErrors.webhook.invalidUrl');
    setNameError(nextNameError);
    setUrlError(nextUrlError);
    if (nextNameError || nextUrlError) return false;

    const payload = {
      name, url, eventKeys: draft.eventKeys, isEnabled: draft.isEnabled,
    };
    if (draft.id) return update(draft.id, payload);

    const created = await create(payload);
    if (!created) return false;   // hata toast'ı interceptor'da; çekmece açık kalır
    reveal(created, false);
    return true;
  }, [draft, create, update, reveal, t]);

  const handleRotateConfirm = useCallback(async () => {
    if (!rotateTarget) return;
    setBusy(true);
    const created = await rotate(rotateTarget.id);
    setBusy(false);
    setRotateTarget(null);
    if (created) reveal(created, true);
  }, [rotateTarget, rotate, reveal]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setBusy(true);
    await remove(deleteTarget.id);
    setBusy(false);
    setDeleteTarget(null);
  }, [deleteTarget, remove]);

  const handleTest = useCallback(async (row) => {
    setTestingId(row.id);
    setTestState(null);
    const result = await test(row.id);
    setTestingId(null);
    setTestState({ name: row.name, ...result });
  }, [test]);

  const handleCopy = useCallback(async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.plainSecret);
      setCopied(true);
    } catch {
      // Pano erişimi yoksa kullanıcı sırrı elle seçer (kutu tek tıkla tümünü seçer).
    }
  }, [revealed]);

  const columns = useMemo(() => {
    const cols = [
      {
        name: 'name',
        label: t('webhooks.name'),
        options: { filter: false, sort: false },
      },
      {
        name: 'url',
        label: t('webhooks.url'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v) => (v ? <UrlText title={v} data-role="webhook-url">{v}</UrlText> : '-'),
        },
      },
      {
        name: 'eventKeys',
        label: t('webhooks.events'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v) => {
            const keys = Array.isArray(v) ? v : [];
            if (keys.length === 0) return <Muted data-role="webhook-events">{t('webhooks.allPmEvents')}</Muted>;
            const text = keys.map((k) => eventLabel(t, k)).join(', ');
            return (
              <Tooltip text={text}>
                <span data-role="webhook-events">{t('webhooks.eventCount', { count: keys.length })}</span>
              </Tooltip>
            );
          },
        },
      },
      {
        name: 'status',
        label: t('webhooks.status'),
        options: {
          filter: false,
          sort: false,
          download: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            const state = endpointState(row);
            const cfg = ENDPOINT_STATE_COLORS[state];
            const badge = (
              <Badge data-role="webhook-state" data-state={state} $color={cfg.color} $bg={cfg.bg}>
                {t(`webhooks.state.${state}`)}
              </Badge>
            );
            return state === 'autoDisabled' ? <Tooltip text={t('webhooks.disabledReason')}>{badge}</Tooltip> : badge;
          },
        },
      },
      {
        name: 'successRate24h',
        label: t('webhooks.successRate'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const rate = formatSuccessRate(v, lang);
            if (!rate) return <Muted data-role="webhook-success-rate">-</Muted>;
            return (
              <Tooltip text={t('webhooks.successRateDetail', { succeeded: row?.succeeded24h ?? 0, failed: row?.failed24h ?? 0 })}>
                <span data-role="webhook-success-rate">{rate}</span>
              </Tooltip>
            );
          },
        },
      },
      {
        name: 'lastStatus',
        label: t('webhooks.lastDelivery'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!v) return <Muted>{t('webhooks.noDeliveries')}</Muted>;
            const cfg = DELIVERY_STATUS_COLORS[v] || DELIVERY_STATUS_COLORS.pending;
            return (
              <Stack>
                <Badge data-role="webhook-last-status" $color={cfg.color} $bg={cfg.bg}>
                  {t(`webhooks.deliveryStatus.${v}`)}
                </Badge>
                {row?.lastDeliveryAt && <Muted>{formatDateTime(row.lastDeliveryAt, lang)}</Muted>}
              </Stack>
            );
          },
        },
      },
      {
        name: 'dlqCount',
        label: t('webhooks.dlqCount'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v) => (v > 0
            ? (
              <Badge data-role="webhook-dlq-count" $color={DELIVERY_STATUS_COLORS.dlq.color} $bg={DELIVERY_STATUS_COLORS.dlq.bg}>
                {v}
              </Badge>
            )
            : <Muted>0</Muted>),
        },
      },
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false,
          sort: false,
          download: false,
          print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return (
              <RowActions>
                {/* Teslimat günlüğü okumadır — PA.Read yeter. */}
                <Tooltip text={t('webhooks.deliveries')}>
                  <IconButton
                    type="button"
                    size="sm"
                    data-role={`webhook-deliveries-${row.id}`}
                    aria-label={t('webhooks.deliveries')}
                    onClick={() => setDeliveriesTarget(row)}
                  >
                    <History size={16} />
                  </IconButton>
                </Tooltip>
                {canManage && (
                  <>
                    <Tooltip text={t('webhooks.test')}>
                      <IconButton
                        type="button"
                        size="sm"
                        data-role={`webhook-test-${row.id}`}
                        aria-label={t('webhooks.test')}
                        disabled={testingId === row.id}
                        onClick={() => handleTest(row)}
                      >
                        <Send size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip text={t('webhooks.edit')}>
                      <IconButton
                        type="button"
                        size="sm"
                        data-role={`webhook-edit-${row.id}`}
                        aria-label={t('webhooks.edit')}
                        onClick={() => openEdit(row)}
                      >
                        <Pencil size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip text={t('webhooks.rotateSecret')}>
                      <IconButton
                        type="button"
                        size="sm"
                        data-role={`webhook-rotate-${row.id}`}
                        aria-label={t('webhooks.rotateSecret')}
                        onClick={() => setRotateTarget(row)}
                      >
                        <RefreshCw size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip text={t('webhooks.delete')}>
                      <IconButton
                        type="button"
                        size="sm"
                        variant="danger"
                        data-role={`webhook-delete-${row.id}`}
                        aria-label={t('webhooks.delete')}
                        onClick={() => setDeleteTarget(row)}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </RowActions>
            );
          },
        },
      },
    ];
    return cols;
  }, [t, lang, data, canManage, testingId, handleTest, openEdit]);

  const targetRef = (row) => (row ? (
    <TargetRef>
      {row.name}
      {' · '}
      <span>{row.url}</span>
    </TargetRef>
  ) : null);

  // "Yüklenemedi" ile "hiç uç yok" AYRI: yükleme başarısızsa boş-durum metni söylenmez.
  const isEmpty = !fetching && !failed && data.length === 0;

  const testTone = { success: 'success', targetError: 'warning' }[testState?.kind] || 'error';
  const TestIcon = { success: CheckCircle2, targetError: AlertTriangle }[testState?.kind] || XCircle;

  return (
    <Container>
      {canManage && (
        <GenericFormDrawer
          open={formOpen}
          onClose={closeForm}
          title={draft.id ? t('webhooks.edit') : t('webhooks.new')}
          schema={[]}
          onSave={handleSave}
        >
          {draft.id && draft.autoDisabledAt && !draft.isEnabled && (
            <DrawerNotice data-role="webhook-auto-disabled-notice">
              {t('webhooks.disabledReason')}
              {' '}
              {t('webhooks.reEnableHint')}
            </DrawerNotice>
          )}
          <FieldGroup>
            <TextInput
              label={t('webhooks.name')}
              value={draft.name}
              onChange={(e) => { setField('name', e.target.value); setNameError(''); }}
              maxLength={WEBHOOK_NAME_MAX}
              required
              error={nameError || undefined}
              data-role="webhook-name"
            />
          </FieldGroup>
          <FieldGroup>
            <TextInput
              label={t('webhooks.url')}
              placeholder="https://example.com/webhooks/flexbee"
              value={draft.url}
              onChange={(e) => { setField('url', e.target.value); setUrlError(''); }}
              required
              error={urlError || undefined}
              data-role="webhook-url-input"
            />
            <Hint>{t('webhooks.urlHint')}</Hint>
          </FieldGroup>
          <FieldGroup>
            <AsyncGenericSelect
              label={t('webhooks.events')}
              placeholder={t('webhooks.searchEvents')}
              value={draft.eventKeys}
              onChange={(v) => setField('eventKeys', Array.isArray(v) ? v : [])}
              onSearch={eventAsync.onSearch}
              onGetById={eventAsync.onGetById}
              isMulti
            />
            <Hint>{t('webhooks.eventsHint')}</Hint>
          </FieldGroup>
          <FieldGroup>
            <CheckboxInput
              label={t('webhooks.enabled')}
              checked={draft.isEnabled}
              onChange={(v) => setField('isEnabled', v)}
              data-role="webhook-enabled"
            />
          </FieldGroup>
          {!draft.id && <Hint data-role="webhook-secret-generated-hint">{t('webhooks.secretGeneratedHint')}</Hint>}
        </GenericFormDrawer>
      )}

      <ConfirmDialog
        isOpen={!!rotateTarget}
        type="info"
        title={t('webhooks.rotateSecret')}
        message={t('webhooks.rotateInfo')}
        confirmText={t('webhooks.rotateSecret')}
        cancelText={t('webhooks.dismiss')}
        onConfirm={handleRotateConfirm}
        onCancel={() => setRotateTarget(null)}
        loading={busy}
      >
        {targetRef(rotateTarget)}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={t('webhooks.deleteTitle')}
        message={t('webhooks.deleteConfirm')}
        confirmText={t('webhooks.delete')}
        cancelText={t('webhooks.dismiss')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        loading={busy}
      >
        {targetRef(deleteTarget)}
      </ConfirmDialog>

      <WebhookDeliveriesDrawer
        endpoint={deliveriesTarget}
        catalog={catalog}
        canReplay={canManage}
        onClose={() => setDeliveriesTarget(null)}
      />

      {failed && <LoadErrorBanner message={t('webhooks.loadFailed')} />}

      {revealed && (
        <RevealPanel data-role="webhook-secret-reveal" role="status">
          <RevealHeader>
            <KeyRound size={16} />
            <RevealTitle>
              {t('webhooks.secretTitle')}
              {': '}
              {revealed.name}
            </RevealTitle>
            <Tooltip text={t('common.close')}>
              <IconButton
                type="button"
                size="sm"
                data-role="close-plain-secret"
                aria-label={t('common.close')}
                onClick={closeReveal}
              >
                <X size={16} />
              </IconButton>
            </Tooltip>
          </RevealHeader>
          <RevealText data-role="secret-once">{t('webhooks.secretOnce')}</RevealText>
          {revealed.rotated && <RevealText data-role="rotate-info">{t('webhooks.rotateInfo')}</RevealText>}
          <SecretRow>
            <PlainSecret data-role="plain-secret">{revealed.plainSecret}</PlainSecret>
            <Button variant="outline" type="button" data-role="copy-plain-secret" onClick={handleCopy}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? t('webhooks.copied') : t('webhooks.copy')}
            </Button>
          </SecretRow>
        </RevealPanel>
      )}

      {testState && (
        <TestResult data-role={`webhook-test-result-${testState.kind}`} $tone={testTone} role="status">
          <TestIcon size={16} />
          <span>
            {testState.name}
            {': '}
            {testState.kind === 'success' && t('webhooks.testSuccess', { responseStatus: testState.responseStatus })}
            {testState.kind === 'targetError' && t('webhooks.testTargetError', {
              responseStatus: testState.responseStatus, error: testState.error || '-',
            })}
            {testState.kind === 'failed' && t('responseErrors.webhook.testFailed')}
          </span>
          <Tooltip text={t('common.close')}>
            <IconButton type="button" size="sm" aria-label={t('common.close')} onClick={() => setTestState(null)}>
              <X size={16} />
            </IconButton>
          </Tooltip>
        </TestResult>
      )}

      <Notice data-role="webhooks-notice">
        <Info size={16} />
        <NoticeBody>
          {isEmpty && <EmptyLine data-role="webhooks-empty">{t('webhooks.empty')}</EmptyLine>}
          <span data-role="webhooks-signature-notice">{t('webhooks.signatureNotice')}</span>
        </NoticeBody>
      </Notice>

      <DataTable
        title={t('webhooks.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        serverSide={false}
        disableFilters
        canCreateNewRecord={canManage}
        newButtonLabel={t('webhooks.new')}
        onNewButtonClick={openCreate}
        tableBodyMaxHeight="calc(100vh - 360px)"
      />
    </Container>
  );
}
